import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/adventures`.
 *
 * Backend contract mirrors `/api/admin/globals/tasks` — same 3-phase
 * versioning model (draft `current_version` → publish → tenants sync).
 * Keep this file in lockstep with the route handlers — in particular the
 * PATCH body's `base_version` + 409 `{error:'version_conflict', current_version}`
 * shape, which the edit page relies on to distinguish a stale-save from
 * any other failure.
 *
 * Round 4 additions vs. tasks:
 * - `language_code` — adventures are localized (tasks aren't, at least not yet)
 * - `adventure_config_json` — JSONB blob driving adventure screens (Monaco
 *   editor lands in R5; R4 uses a plain validated textarea)
 * - `cms_component_code` — opt-in reference to a CMS component template
 */

/** History-row shape returned inside the GET /:code envelope. */
export interface AdventureHistoryRow {
  version: number;
  adventure_name: string | null;
  language_code: string;
  adventure_config_json: object | null;
  cms_component_code: string | null;
  platform_adventure_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminAdventureRow {
  adventure_code: string;
  adventure_name: string | null;
  language_code: string;
  adventure_config_json: object | null;
  cms_component_code: string | null;
  platform_adventure_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  /** Derived on the server; `unpublished` becomes wire-able in Round 4. */
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminAdventureDetail extends AdminAdventureRow {
  history: AdventureHistoryRow[];
}

export interface ListAdventuresResponse {
  rows: AdminAdventureRow[];
}

export interface ListAdventuresParams {
  sort?: 'adventure_code' | 'adventure_name' | 'updated_at' | 'current_version';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateAdventurePayload {
  adventure_code: string;
  adventure_name: string;
  language_code: string;
  adventure_config_json?: object | null;
  cms_component_code?: string | null;
  change_summary?: string;
}

export interface UpdateAdventurePayload {
  base_version: number;
  adventure_name?: string;
  language_code?: string;
  adventure_config_json?: object | null;
  cms_component_code?: string | null;
  /**
   * Active flag — used by Reactivate / soft-Deactivate flows. Backend
   * accepts it on the same PATCH endpoint.
   */
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback                                                  */
/* ------------------------------------------------------------------ */

export interface PublishAdventureEntityRequest {
  adventure_code: string;
  base_version: number;
}

export interface PublishAdventuresRequest {
  entities: PublishAdventureEntityRequest[];
  /**
   * Optional client-minted batch id. Dialog mints once per open and
   * reuses across retries so the server can dedupe / group partial
   * failures under one history row.
   */
  batch_id?: string;
  change_summary?: string;
}

export interface PublishAdventureEntityResult {
  adventure_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishAdventuresResponse {
  batch_id: string;
  results: PublishAdventureEntityResult[];
}

export interface RollbackAdventurePayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

export const adminAdventureApi = {
  list: async (params: ListAdventuresParams = {}): Promise<AdminAdventureRow[]> => {
    const res = await axiosClient.get<ListAdventuresResponse>(
      '/api/admin/globals/adventures',
      { params },
    );
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminAdventureDetail> => {
    const res = await axiosClient.get<AdminAdventureDetail>(
      `/api/admin/globals/adventures/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (payload: CreateAdventurePayload): Promise<AdminAdventureRow> => {
    const res = await axiosClient.post<AdminAdventureRow>(
      '/api/admin/globals/adventures',
      payload,
    );
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateAdventurePayload,
  ): Promise<AdminAdventureRow> => {
    const res = await axiosClient.patch<AdminAdventureRow>(
      `/api/admin/globals/adventures/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (payload: PublishAdventuresRequest): Promise<PublishAdventuresResponse> => {
    const res = await axiosClient.post<PublishAdventuresResponse>(
      '/api/admin/globals/adventures/publish',
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishAdventuresResponse> => {
    return adminAdventureApi.publish({
      entities: [{ adventure_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackAdventurePayload,
  ): Promise<AdminAdventureRow> => {
    const res = await axiosClient.post<AdminAdventureRow>(
      `/api/admin/globals/adventures/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
